/**
 * Potential-lead capture.
 *
 * The lead is opened as soon as the partner finishes the contact step, so we
 * keep the funnel entry even when they never verify their phone or fill in
 * the rest of the wizard. Completing the business step calls the same
 * endpoint again with everything we now know — the backend upserts on the
 * phone number, so the second call enriches the existing row.
 *
 * Both calls are best-effort: a failure is logged and the wizard continues.
 */

import { apiClient } from 'shared/lib/apiClient';
import { logger } from 'shared/lib/logger';
import type {
  BusinessCategory,
  DeliveryCompany,
  KosherCertification,
  OpeningHours,
} from 'features/onboarding/types/onboarding.types';

/** What the contact step knows: who they are, and where the store is. */
export interface PotentialLeadContactPayload {
  commercialName: string;
  ownerName: string;
  phoneNumber: string;
  address?: string;
  location?: string;
  latitude?: number | null;
  longitude?: number | null;
}

/** Everything the business step adds on top. */
export interface PotentialLeadBusinessPayload
  extends PotentialLeadContactPayload {
  businessCategory?: BusinessCategory;
  storeType?: string;
  isKosher?: boolean;
  kosherCertifications?: KosherCertification[];
  sensitivities?: string[];
  worksWithOtherDeliveryCompanies?: boolean;
  deliveryCompanies?: DeliveryCompany[];
  otherDeliveryCompanies?: string;
  businessPhoneNumber?: string;
  email?: string;
  workingDays?: string[];
  openingHours?: OpeningHours;
}

export interface SavePotentialLeadResult {
  created: boolean;
  updated?: boolean;
  id?: string;
}

async function post(
  payload: PotentialLeadContactPayload | PotentialLeadBusinessPayload,
): Promise<SavePotentialLeadResult | null> {
  try {
    const { data } = await apiClient.post<SavePotentialLeadResult>(
      '/onboarding/potential-lead',
      payload,
    );
    return data;
  } catch (err) {
    logger.error('[HAAT Partner Connect] potential-lead save failed', {
      message: err instanceof Error ? err.message : String(err),
    });
    return null;
  }
}

export const createPotentialLead = (
  payload: PotentialLeadContactPayload,
): Promise<SavePotentialLeadResult | null> => post(payload);

export const enrichPotentialLead = (
  payload: PotentialLeadBusinessPayload,
): Promise<SavePotentialLeadResult | null> => post(payload);
